import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Building2,
  CreditCard,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Settings,
  ShieldCheck,
  Tags,
  Webhook,
} from 'lucide-react';
import { Logo } from '@/components/Logo';
import { cn } from '@/lib/cn';
import { usePlatformAdmin } from '@/stores/platformAuthStore';
import { usePlatformLogout, usePlatformSupportStats } from '../queries';

const NAV = [
  { to: '/platform', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/platform/tenants', label: 'Workspaces', icon: Building2 },
  { to: '/platform/plans', label: 'Plans', icon: Tags },
  { to: '/platform/subscriptions', label: 'Subscriptions', icon: CreditCard },
  { to: '/platform/support', label: 'Support', icon: LifeBuoy, badge: 'support' },
  { to: '/platform/webhooks', label: 'Webhooks', icon: Webhook },
  { to: '/platform/admins', label: 'Administrators', icon: ShieldCheck },
  { to: '/platform/settings', label: 'Settings', icon: Settings },
];

/**
 * The operator console's frame.
 *
 * Same chrome as the tenant app — light working area, one black rail — because
 * there is one design system here, not two. What distinguishes this console
 * from a customer's workspace is the "Platform" label in the rail and the
 * amber strip along the top, not a different set of colours.
 */
export function PlatformShell() {
  const admin = usePlatformAdmin();
  const navigate = useNavigate();
  const logout = usePlatformLogout();

  // The rail carries the queue depth. An operator leaves this console open all
  // day, and a support queue nobody can see the size of is a support queue
  // that gets checked when somebody complains.
  const { data: support } = usePlatformSupportStats();
  const waiting = support?.open ?? 0;

  return (
    <div className="flex h-dvh overflow-hidden">
      <aside className="flex w-60 shrink-0 flex-col bg-nav **:focus-visible:outline-logo">
        <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-nav-border px-4">
          <Logo wordmark={false} markClassName="h-8 w-8" />
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold leading-tight tracking-tight text-nav-ink">
              Voltra
            </p>
            <p className="truncate text-[11px] leading-tight text-nav-ink-muted">Platform</p>
          </div>
        </div>

        <nav className="scroll-slim-dark min-h-0 flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-0.5">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'relative flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-nav-hover text-logo'
                        : 'text-nav-ink-muted hover:bg-nav-hover hover:text-nav-ink',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          className="absolute left-0 h-5 w-[3px] rounded-r-full bg-logo"
                          aria-hidden
                        />
                      )}
                      <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
                      {item.label}

                      {item.badge === 'support' && waiting > 0 && (
                        <span
                          className="ml-auto rounded-full bg-logo px-1.5 py-0.5 text-[11px] font-semibold leading-none text-nav"
                          aria-label={`${waiting} waiting on us`}
                        >
                          {waiting}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="shrink-0 border-t border-nav-border p-3">
          <p className="truncate text-[13px] font-medium text-nav-ink">
            {admin?.name ?? 'Operator'}
          </p>
          <p className="truncate text-[11px] text-nav-ink-muted">{admin?.email}</p>

          <button
            type="button"
            onClick={() =>
              logout.mutate(undefined, { onSettled: () => navigate('/platform/login') })
            }
            className="mt-3 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium text-nav-ink-muted transition-colors hover:bg-nav-hover hover:text-critical"
          >
            <LogOut className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-page">
        {/*
          The one thing that must never be mistaken for a customer's workspace.
          A persistent strip, not a colour scheme: it survives screenshots, it
          survives a colour-blind operator, and it says WHAT this window is
          rather than leaving the difference to be inferred from a shade.
        */}
        <div className="flex shrink-0 items-center gap-2 border-b border-warning/30 bg-warning-subtle px-6 py-1.5">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-[#8a5f00]" strokeWidth={2} aria-hidden />
          <p className="text-xs font-medium text-[#8a5f00]">
            Platform console — you are looking at every workspace on the platform.
          </p>
        </div>

        <main className="scroll-slim min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
